import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackCampaignVisit } from '../controllers/campaignTrackingController';
import { safeStorage } from '../utils/safeStorage';
import { API_BASE_URL } from '../constants';

const CampaignRedirect = () => {
  const { slug } = useParams();
  const [showOverlay, setShowOverlay] = useState(true);
  const hasRunRef = useRef(false); // 🔒 prevent StrictMode double-run

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const RID_KEY  = `campaign:${slug}:rid`;
    const ST_KEY   = `campaign:${slug}:start`;
    const DEST_KEY = `campaign:${slug}:dest`;

    let startTime   = Number(safeStorage.getItem(ST_KEY)) || Date.now();
    let reportId    = safeStorage.getItem(RID_KEY) || null;
    let destination = safeStorage.getItem(DEST_KEY) || null;
    let sent = false;

    const persist = () => {
      safeStorage.setItem(ST_KEY, String(startTime));
      if (reportId)    safeStorage.setItem(RID_KEY, reportId);
      if (destination) safeStorage.setItem(DEST_KEY, destination);
    };

    const clearPersisted = () => {
      safeStorage.removeItem(ST_KEY);
      safeStorage.removeItem(RID_KEY);
      safeStorage.removeItem(DEST_KEY);
    };

    const sendEngagement = () => {
      if (sent || !reportId) return;
      sent = true;

      const engagementMs = Math.max(0, Date.now() - startTime);
      const payload = JSON.stringify({ engagementMs });
      const blob = new Blob([payload], { type: 'application/json' });

      const ok =
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(
          `${API_BASE_URL}api/campaigns/public/reports/${reportId}/engagement`,
          blob
        );

      if (!ok) {
        fetch(`${API_BASE_URL}api/campaigns/public/reports/${reportId}/engagement`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
          credentials: 'omit',
        }).catch(() => {});
      }

      clearPersisted();
    };

    // call sendEngagement right before leaving the page as well
    const redirect = (url) => {
      setTimeout(() => {
        sendEngagement();
        setShowOverlay(false);
        window.location.href = url;
      }, 1500);
    };

    const connectAndRedirect = async () => {
      try {
        if (reportId && destination) {
          redirect(destination);
          return;
        }

        const { destinationUrl, reportId: id } = await trackCampaignVisit(slug);
        reportId    = id;
        destination = destinationUrl;
        persist();

        redirect(destinationUrl);
      } catch (err) {
        console.error('Campaign tracking failed:', err);
        // optional fallback:
        // window.location.href = '/';
      }
    };

    const handleBeforeUnload     = () => sendEngagement();
    const handleVisibilityChange = () => { if (document.visibilityState === 'hidden') sendEngagement(); };
    const handlePageHide         = () => sendEngagement();

    persist();
    connectAndRedirect();

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [slug]);

  return (
    <>
      <AnimatePresence>
        {showOverlay && (
          <motion.div
            key="campaign-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28 }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background:
                'linear-gradient(180deg, rgba(16,18,27,0.95) 0%, rgba(16,18,27,0.92) 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
            }}
            aria-live="polite"
            role="status"
          >
            {/* JUST THREE DOTS */}
            <div style={{ display: 'flex', gap: 10 }}>
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: '#ffffff',
                    display: 'inline-block',
                    opacity: 0.3,
                  }}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CampaignRedirect;
